/**
 * Todae store — catalog sync + pricing policy constants (Stage 1, plan §3).
 *
 * Single source of truth shared by scripts/sync-shop-products.mjs (imported
 * directly by plain `node` — Node >= 23.6 strips the types natively) AND by
 * app/edge code. Because of that dual life this file must stay dependency-free:
 * NO imports, NO `import.meta.env`, erasable-TS syntax only (no enums).
 *
 * All money is BASE ZAR excluding VAT (binding decision D3; legal seat South
 * Africa — SARS VAT at 15% is applied later via a global config, never here).
 */

/**
 * The 4 distributor catalogues whose single price column is a DEALER BUY
 * price, not retail (plan §3 — "Medium Corporate" is an IDS dealer tier,
 * "Reaction" = armed-response trade buyers). Rows from these lists get
 * `retail_excl_zar = list price x category markup`; the list price is stored
 * as cost_zar/trade_excl_zar. Matched against warehouse.json `sourceFile` by
 * pattern (filenames carry dates that change every price-list revision).
 *
 * PROVISIONAL until owner decision #13 (supplier confirmation of each list's
 * price basis) — the sync script prints a warning on every run until then.
 */
export const DEALER_PRICE_LISTS = [
  { key: 'comms', label: 'Comms Installer Price List', pattern: /comms\s+installer\s+price\s+list/i },
  { key: 'ids', label: 'IDS Medium Corporate Pricelist', pattern: /ids\s+medium\s+corporate/i },
  { key: 'provision-reaction', label: 'Provision REACTION Price List', pattern: /provision\s+reaction/i },
  { key: 'spectrum-reaction', label: 'Spectrum Reaction Security Price List', pattern: /spectrum\s+reaction/i },
] as const;

/** True when a warehouse.json `sourceFile` is one of the 4 dealer-tier lists. */
export const isDealerList = (sourceFile: string): boolean =>
  DEALER_PRICE_LISTS.some((l) => l.pattern.test(sourceFile || ''));

/**
 * Retail markup applied to dealer-list BUY prices, by pipeline category.
 * Default 1.38 (plan §3: 1.35–1.40, tracking the stock-file median margin).
 * Add per-category overrides here ONLY off a written pricing decision — the
 * sync script reads this table on every run.
 */
export const MARKUPS: { default: number; byCategory: Record<string, number> } = {
  default: 1.38,
  byCategory: {
    // e.g. cable: 1.30, camera: 1.42 — none active until priced deliberately.
  },
};

/** Markup for one pipeline category (falls back to the default). */
export const markupFor = (category: string): number =>
  MARKUPS.byCategory[category] ?? MARKUPS.default;

/**
 * Gross-margin floor: rows where (retail - cost) / retail < this are HELD for
 * admin review by the catalog_import_runs gate (§3 — the >20%-change/→R0 gate
 * alone would pass every zero-margin dealer-list row).
 */
export const MARGIN_FLOOR = 0.15;

/** Default lead time stamped on `supplier`-sourced (on_order) SKUs, days. */
export const SUPPLIER_DEFAULT_LEAD_TIME_DAYS = 7;

/**
 * Representative surveillance HDD for designer-BOM storage lines
 * (`prices.json` has hddPerTbZar: null — without this every imported BOM
 * arrives with an unpriced storage line). NO stocked (source='stock') HDD
 * exists in warehouse.json, so per the Stage 1 fallback this is the CHEAPEST
 * supplier-sourced surveillance HDD: PC5559 — "1TB HDD Surveillance Hard
 * Drive", Provision REACTION list (dealer list ⇒ marked-up retail), on_order.
 * Revisit when a stocked HDD enters the price lists; the sync script warns if
 * this SKU ever disappears from warehouse.json.
 */
export const TODAE_HDD_REPRESENTATIVE_SKU = 'PC5559';

/**
 * US-market eligibility audit (docs/todae-market-audit.md, 2026-07-19).
 * FCC Covered List brands + their retail OEM aliases are hard-forced
 * market='za' at every layer (§9) — they may NEVER be 'us'/'both'.
 * Matched case-insensitively against warehouse.json `brand`.
 */
export const FCC_COVERED_BRANDS = [
  'Hikvision',
  'Dahua',
  'HiLook',
  'HiWatch',
  'Imou',
  'Lorex',
  'Amcrest',
] as const;

/**
 * Brands AUDITED as non-Covered and cleared for market='both' (ZA + US
 * display). Deliberately conservative: unbranded/generic SKUs are NOT
 * included (country of origin unverifiable — a deviation from the plan's
 * looser "generic cable/networking" wording, recorded in the audit note).
 * 'Provision' is the warehouse.json spelling of Provision-ISR.
 */
export const AUDITED_NON_COVERED_BRANDS = [
  'Provision-ISR',
  'Provision',
  'Ajax',
] as const;

/**
 * The 14 pipeline categories (warehouse.json `category`) → customer-facing
 * labels. The pipeline key is what `shop_products.category` stores (stable,
 * URL-safe: /todae/c/:category); the label is display-only.
 *
 * Literal-keyed on purpose (`as const satisfies`, both erasable for Node
 * type stripping): `keyof typeof SHOP_CATEGORIES` is the ONLY category
 * vocabulary, so shop.ts's FACET_CONFIGS (and any future per-category map)
 * can be tsc-checked against it instead of drifting silently.
 */
export const SHOP_CATEGORIES = {
  accessory: 'Accessories',
  camera: 'Cameras',
  detector: 'Detectors & sensors',
  alarm: 'Alarm panels & kits',
  enclosure: 'Enclosures & housings',
  fencing: 'Electric fencing',
  cable: 'Cables',
  recorder: 'Recorders (NVR/DVR)',
  switch: 'PoE switches',
  intercom: 'Intercoms',
  power: 'Power & UPS',
  access: 'Access control',
  network: 'Networking',
  fire: 'Fire detection',
} as const satisfies Record<string, string>;

/** One of the 14 pipeline category keys ('camera', 'alarm', …). */
export type ShopCategoryKey = keyof typeof SHOP_CATEGORIES;
